import {Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output} from "@angular/core";
import {Store} from "@ngrx/store";
import {CopyFundReportAndValuationsActions} from "../../../store/fund.actions";
import {filter, map, Observable, Subscription} from "rxjs";
import {selectAssetNameAndArchivedFromId} from "../../../../asset/store/asset/asset.selectors";
import {AssetListActions} from "../../../../asset/store/asset.actions";
import {FormControl, FormGroup, NonNullableFormBuilder, Validators} from "@angular/forms";
import {CopyConfig, FundInvestmentCopyConfig, FundReportCopyConfig} from "../../../models/copy-config";
import {selectCopyConfig, selectInvestmentsForFundReportToCopy} from "../../../store/fund/fund.selectors";
import {selectSelectedFundReportDates} from "../../../store/fund-report/fund-report.selectors";
import {DateUtil} from "../../../../shared/utils/date-util";
import {reportDateUniqueValidator} from "../../../../shared/utils/form-validators";
import {ReportDatePickerComponent} from "../../../../shared/custom-form-fields/report-date-picker/report-date-picker.component";

export type CopyInvestmentFormGroup = FormGroup<{
    investmentId: FormControl<number>;
    isRealized: FormControl<boolean>;
    copyGpValuation: FormControl<boolean>;
}>;

@Component({
    selector: "valumize-fund-report-copy-dialog",
    templateUrl: "./fund-report-copy-dialog.component.html",
    styleUrl: "./fund-report-copy-dialog.component.scss",
    standalone: false
})
export class FundReportCopyDialogComponent implements OnInit, OnDestroy {
    private readonly store = inject(Store);
    private readonly formBuilder = inject(NonNullableFormBuilder);

    @Input() fundId?: number;

    @Output() completed = new EventEmitter<void>();

    subscriptions: Subscription[] = [];
    copyConfig?: CopyConfig;
    private existingReportDates: string[] = [];

    fundInvestments$ = this.store.select(selectInvestmentsForFundReportToCopy);
    fundInvestmentsColumns = ["assetId", "assetName", "ownershipStake", "investmentDate", "isRealized", "copyGpValuation"];

    reportCopyForm = this.formBuilder.group({
        reportDate: this.formBuilder.control<Date | null>(null, {
            validators: [Validators.required, reportDateUniqueValidator(() => this.existingReportDates)]
        }),
        copyInvestments: this.formBuilder.control<boolean>(false),
        investments: this.formBuilder.array<CopyInvestmentFormGroup>([]),
    });

    get investmentsFormArray() {
        return this.reportCopyForm.controls.investments;
    }

    constructor() {
        this.store.dispatch(AssetListActions.load({}));
    }

    ngOnInit() {
        // Cache the fund's existing report dates for the uniqueness validator and re-validate on change.
        this.subscriptions.push(
            this.store.select(selectSelectedFundReportDates).subscribe(reportDates => {
                this.existingReportDates = reportDates;
                this.reportCopyForm.controls.reportDate.updateValueAndValidity({emitEvent: false});
            }));

        this.subscriptions.push(
            this.store.select(selectCopyConfig).pipe(
                filter(copyConfig => !!this.fundId && !!copyConfig.fundReportCopyConfig?.fundReportId),
                map(copyConfig => {
                    this.copyConfig = copyConfig
                    // Seed the editable new-report-date input (WI-1239), and only while the user has not
                    // touched it: a genuine PAST date from the copy config if it has one, otherwise the
                    // latest past quarter-end, because today and future dates are not selectable.
                    const reportDateControl = this.reportCopyForm.controls.reportDate;
                    if (reportDateControl.pristine && reportDateControl.value == null) {
                        const configReportDate = copyConfig.fundReportCopyConfig?.reportDate;
                        const seedIso = configReportDate && configReportDate < DateUtil.isoDate()
                            ? configReportDate
                            : ReportDatePickerComponent.latestPastQuarterIso();
                        reportDateControl.setValue(DateUtil.fromIsoDate(seedIso)?.toJSDate() ?? null);
                    }
                    this.store.dispatch(CopyFundReportAndValuationsActions.loadInvestments({fundId: this.fundId!, fundReportId: copyConfig.fundReportCopyConfig!.fundReportId}));
                })).subscribe());

        this.subscriptions.push(
            this.fundInvestments$.pipe(map(investments => {
                this.investmentsFormArray.clear();
                investments.forEach(investment => {
                    if (!investment.id) return;
                    const investmentCopyConfig = this.copyConfig?.fundReportCopyConfig?.fundInvestmentsCopyConfig.find(config => config.investmentId === investment.id);
                    const isRealized = investment.isRealized.value === true;
                    const group: CopyInvestmentFormGroup = this.formBuilder.group({
                        investmentId: this.formBuilder.control(investment.id),
                        isRealized: this.formBuilder.control({value: investmentCopyConfig?.isRealized ?? investment.isRealized.value ?? false, disabled: isRealized}),
                        copyGpValuation: this.formBuilder.control(investmentCopyConfig?.copyGpValuation ?? false),
                    });
                    this.investmentsFormArray.push(group);
                })
            })).subscribe());
    }

    getAssetNameAndArchivedById(assetId: number): Observable<{ name: string; isArchived: boolean }> {
        return this.store.select(selectAssetNameAndArchivedFromId(assetId));
    }

    getIsRealizedControl = (index: number): FormControl<boolean> => this.investmentsFormArray.at(index).controls.isRealized;

    getCopyGpValuationControl = (index: number): FormControl<boolean> => this.investmentsFormArray.at(index).controls.copyGpValuation;

    navigateToFundValuationCopyDialog() {
        this.updateCopyConfig();
        this.completed.emit();
    }

    private updateCopyConfig() {
        const fundReportCopyConfig = this.copyConfig?.fundReportCopyConfig;
        if (!fundReportCopyConfig || !this.reportCopyForm.valid) {
            return;
        }

        const fundInvestmentsCopyConfig: FundInvestmentCopyConfig[] = this.reportCopyForm.controls.copyInvestments.value ? this.investmentsFormArray.getRawValue() : [];
        // Persist the (possibly edited) new report date entered in step 1 (WI-1239).
        const reportDate = DateUtil.toIsoDate(this.reportCopyForm.controls.reportDate.value) ?? fundReportCopyConfig.reportDate;
        const updatedFundReportCopyConfig: FundReportCopyConfig = {...fundReportCopyConfig, reportDate, fundInvestmentsCopyConfig}
        this.copyConfig = {...this.copyConfig, fundReportCopyConfig: updatedFundReportCopyConfig};

        this.store.dispatch(CopyFundReportAndValuationsActions.updateCopyConfig({copyConfig: this.copyConfig}));
    }

    toggleAllCopyGpValuation(checked: boolean): void {
        const len = this.investmentsFormArray.length;
        for (let i = 0; i < len; i++) {
            this.investmentsFormArray.at(i).controls.copyGpValuation.setValue(checked, {emitEvent: false});
        }
        this.reportCopyForm.updateValueAndValidity({emitEvent: true});
    }

    isAllCopyGpValuationSelected(): boolean {
        const len = this.investmentsFormArray.length;
        if (len === 0) return false;
        for (let i = 0; i < len; i++) {
            if (!this.investmentsFormArray.at(i).controls.copyGpValuation.value) {
                return false;
            }
        }
        return true;
    }

    toggleAllRealized(checked: boolean): void {
        const len = this.investmentsFormArray.length;
        for (let i = 0; i < len; i++) {
            this.investmentsFormArray.at(i).controls.isRealized.setValue(checked, {emitEvent: false});
        }
        this.reportCopyForm.updateValueAndValidity({emitEvent: true});
    }

    isAllRealizedSelected(): boolean {
        const len = this.investmentsFormArray.length;
        if (len === 0) return false;
        for (let i = 0; i < len; i++) {
            if (!this.investmentsFormArray.at(i).controls.isRealized.value) {
                return false;
            }
        }
        return true;
    }

    ngOnDestroy = () => this.subscriptions.forEach(sub => sub.unsubscribe());
}
